import React from 'react';
import { Helmet } from 'react-helmet';
import { Smartphone, LucideIcon } from 'lucide-react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import PartnershipCard, { PartnershipProduct } from '../components/partners/PartnershipCard';
import PartnerInquiryForm from '../components/partners/PartnerInquiryForm';
import { COMMUNITY_STATS } from '../data/stats';

type StatTile = {
  value?: string;
  icon?: LucideIcon;
  label: string;
};

const statTiles: StatTile[] = [
  { value: COMMUNITY_STATS.followers, label: 'Social followers' },
  { value: COMMUNITY_STATS.cities, label: 'Cities worldwide' },
  { icon: Smartphone, label: 'A dedicated mobile app' },
  { value: COMMUNITY_STATS.growth, label: 'Growth in six months' },
];

// Add, reorder, or edit partnership products here — the grid lays itself out.
const partnershipProducts: PartnershipProduct[] = [
  {
    title: 'Member Offers',
    copy: 'Exclusive rates and perks for DINK Social members, featured in the DINK Partners Hub inside the app.',
  },
  {
    title: 'Hangout Sponsorship',
    copy: `Host or sponsor a local hangout in your city. Our members meet in real life across ${COMMUNITY_STATS.cities} cities.`,
  },
  {
    title: 'DINK Weekends',
    copy: 'Destination trips built with a venue partner, where a group of our couples takes over your property.',
  },
  {
    title: 'Community Activations',
    copy: `Co-created content, challenges and giveaways across the app and our ${COMMUNITY_STATS.followers} social audience.`,
  },
];

// ─── Placeholder: community data section ─────────────────────────
// Reserved slot between the intro and the partnership cards. A community
// data chart lands here in a later release. Renders nothing in V1.
const CommunityDataSection: React.FC = () => null;

// ─── Placeholder: partner logo row ───────────────────────────────
// Reserved slot below the partnership cards for a "Partners include" logo
// row, expected within a couple of months. Renders nothing in V1.
const PartnerLogoRow: React.FC = () => null;

const PartnersPage: React.FC = () => {
  return (
    <div className="font-display text-ink bg-white min-h-screen">
      <Helmet>
        <title>Partnerships | DINK Social</title>
        <meta
          name="description"
          content={`Partner with DINK Social, the community for childfree couples. ${COMMUNITY_STATS.couples} active couples across ${COMMUNITY_STATS.cities} cities. Member offers, sponsored hangouts, DINK Weekends, and community activations.`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Partnerships | DINK Social" />
        <meta
          property="og:description"
          content="Partner with DINK Social, the community for childfree couples. Member offers, sponsored hangouts, DINK Weekends, and community activations."
        />
        <meta property="og:url" content="https://dinksocial.com/partners" />
        <meta property="og:site_name" content="DINK Social" />
        <meta property="og:image" content="https://dinksocial.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Partnerships | DINK Social" />
        <meta
          name="twitter:description"
          content="Partner with DINK Social, the community for childfree couples. Member offers, sponsored hangouts, DINK Weekends, and community activations."
        />
        <meta name="twitter:image" content="https://dinksocial.com/og-image.jpg" />
        <link rel="canonical" href="https://dinksocial.com/partners" />
      </Helmet>

      <Nav />

      {/* ─── HERO ──────────────────────────────────────────── */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 px-6 md:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1
            className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px] mb-5"
            style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            {'Work with '}
            <img
              src="/DINKSocial_logo_plum.svg"
              alt="DINK Social"
              className="inline-block h-[1.02em] w-auto align-[-0.13em]"
            />
          </h1>
          <p
            className="font-playfair italic font-medium text-ink/85 text-[20px] md:text-[24px]"
            style={{ lineHeight: 1.3, letterSpacing: '-0.015em' }}
          >
            A community of childfree couples building a full life together, and
            the brands that help them do it.
          </p>
        </div>
      </section>

      {/* ─── STAT TILES ────────────────────────────────────── */}
      <section className="pb-12 md:pb-16 px-6 md:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {statTiles.map((s) => (
              <div
                key={s.label}
                className="border border-ink/10 rounded-3xl p-6 md:p-7 text-center"
              >
                {s.icon ? (
                  <div
                    className="flex items-center justify-center text-primary text-[32px] md:text-[40px]"
                    style={{ height: '1.05em' }}
                  >
                    <s.icon className="h-8 md:h-10 w-auto" strokeWidth={1.8} />
                  </div>
                ) : (
                  <div
                    className="font-display font-medium text-primary text-[32px] md:text-[40px]"
                    style={{ lineHeight: 1.05, letterSpacing: '-0.02em' }}
                  >
                    {s.value}
                  </div>
                )}
                <div className="text-[10px] md:text-[11px] uppercase tracking-[0.22em] font-medium text-ink/60 mt-2">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── INTRO ─────────────────────────────────────────── */}
      <section className="pb-10 md:pb-16 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <p
            className="font-display font-medium text-ink text-[24px] md:text-[30px] mb-6"
            style={{ lineHeight: 1.2, letterSpacing: '-0.02em' }}
          >
            DINK Social is where childfree couples find their people.
          </p>
          <p
            className="text-[17px] md:text-[19px] text-ink/70 mb-5"
            style={{ lineHeight: 1.55 }}
          >
            {/* Single template literal on purpose: adjacent JSX text nodes break
                react-snap hydration (serialized HTML merges them). */}
            {`We launched in January 2026. Since then we've grown to more than 100,000 followers on Instagram and TikTok, with a dedicated mobile app connecting couples across ${COMMUNITY_STATS.cities} cities. Almost all of that growth has come through word of mouth.`}
          </p>
          <p
            className="text-[17px] md:text-[19px] text-ink/70 mb-5"
            style={{ lineHeight: 1.55 }}
          >
            DINK (dual-income, no-kids) couples hold a median net worth of about
            $399K, roughly $150K more than couples with children. They spend 4x
            the average on dining out, around $2,000 per trip with multiple
            trips a year, and 88% direct their income toward experiences.
            Fortune calls DINKs "the new one percent."
          </p>
          <p className="text-[13px] text-ink/50" style={{ lineHeight: 1.5 }}>
            Sources: Harris Poll via Fortune (2024); U.S. Federal Reserve data
            via Fortune (2023).
          </p>
        </div>
      </section>

      <CommunityDataSection />

      {/* ─── HOW WE WORK WITH PARTNERS ─────────────────────── */}
      <section className="py-10 md:py-16 px-6 md:px-10">
        <div className="max-w-5xl mx-auto">
          <h2
            className="font-display font-medium text-ink text-[32px] md:text-[44px] mb-8 md:mb-12"
            style={{ lineHeight: 1.05, letterSpacing: '-0.02em' }}
          >
            How we work with partners
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {partnershipProducts.map((p) => (
              <PartnershipCard key={p.title} {...p} />
            ))}
          </div>
        </div>
      </section>

      <PartnerLogoRow />

      {/* ─── WHAT WE LOOK FOR ──────────────────────────────── */}
      <section className="py-10 md:py-16 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <h2
            className="font-display font-medium text-ink text-[32px] md:text-[44px] mb-6 md:mb-8"
            style={{ lineHeight: 1.05, letterSpacing: '-0.02em' }}
          >
            What we look for
          </h2>
          <p
            className="text-[17px] md:text-[19px] text-ink/70 mb-5"
            style={{ lineHeight: 1.55 }}
          >
            We're careful about who we work with. DINK Social was built for
            connection, not content farming, so every partner has to pass a
            simple test: does this genuinely make life better for couples
            without kids? If it helps them live the DINK life together, or
            helps them get ready for what comes next, we'd love to hear from
            you.
          </p>
          <p
            className="font-display font-medium text-ink text-[17px] md:text-[19px]"
            style={{ lineHeight: 1.55 }}
          >
            We feature one founding partner per category at launch.
          </p>
        </div>
      </section>

      {/* ─── LET'S TALK ────────────────────────────────────── */}
      <section className="py-10 md:py-20 px-6 md:px-10">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <h2
              className="font-display font-medium text-ink text-[32px] md:text-[44px] mb-4"
              style={{ lineHeight: 1.05, letterSpacing: '-0.02em' }}
            >
              Let's talk
            </h2>
            <p
              className="font-playfair italic font-medium text-ink/85 text-[20px] md:text-[24px]"
              style={{ lineHeight: 1.3, letterSpacing: '-0.015em' }}
            >
              Tell us what you have in mind
            </p>
          </div>

          <PartnerInquiryForm />

          <p
            className="text-[15px] md:text-[16px] text-ink/70 mt-8"
            style={{ lineHeight: 1.55 }}
          >
            {'Or email us directly at '}
            <a
              href="mailto:partnerships@dinksocial.com"
              className="text-primary font-medium hover:text-primary-dark hover:underline transition-colors"
            >
              partnerships@dinksocial.com
            </a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnersPage;
